import { useEffect, type FC } from 'react'

import { Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import Highlight from '@/components/Highlight'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSearch, useSortParams } from '@/hooks/searchParams'
import { useLocalDate } from '@/hooks/use-local-date'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { useProfile } from '@/hooks/user'

import { useReportsContext } from '../index.context'

import adminQueries from '../../queries'
import type { ReportFilters } from '../../resources'
import adminResources from '../../resources'

export const NewNumberOrders: FC<{ filters: ReportFilters }> = ({ filters }) => {
  const locale = useProfile()?.locale
  const { formatDate } = useLocalDate()
  const { searchFilter } = useSearch()
  const { isReportListLoading, setCount, setIsReportListLoading } = useReportsContext()

  const { data: reports = [] } = useQuery(
    adminQueries.adminNewNumberOrdersReports(filters),
    async () => {
      const reports = await adminResources.getNewNumberOrdersReports(filters)
      setIsReportListLoading(false)
      return reports
    },
    { enabled: Boolean(filters.fromDate && filters.toDate) }
  )
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof reportsFiltered)[number]>()

  useEffect(() => {
    setCount(reports.length)
  }, [reports, setCount])

  const reportsFormatted = reports.map(report => ({
    id: report.id,
    name: report.simName,
    phoneNo: report.phoneNo,
    employeeNumber: report.employeeNumber,
    contactNumber: report.contactNumber,
    activationDate: report.activationDate,
    department: report.department.name,
    departmentCode: report.department.code,
    salaryDeductionProfile: report.salaryDeductionProfile?.name,
    devicePolicy: report.devicePolicy?.name,
  }))

  const stringMatcher = (item: (typeof reportsFormatted)[number]) => [
    item.name,
    item.phoneNo,
    item.employeeNumber,
    item.contactNumber,
    formatDate({
      date: item.activationDate,
      locale,
    }),
    item.department,
    item.departmentCode,
    item.salaryDeductionProfile,
    item.devicePolicy,
  ]

  const reportsFiltered = reportsFormatted.filter(reportsObject =>
    getQueryFilter(searchFilter, stringMatcher)(reportsObject)
  )
  const sortedList = getSortedList({
    list: reportsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)
  const total = reportsFiltered.length

  return (
    <>
      <ResultCount count={total} />
      {total > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th onClick={handleSort} id="name">
                <Translation id="Employee" />
              </Th>
              <Th onClick={handleSort} id="contactNumber">
                <Translation id="Contact Number" />
              </Th>
              <Th onClick={handleSort} id="phoneNo">
                <Translation id="Phone Number" />
              </Th>
              <Th onClick={handleSort} id="activationDate">
                <Translation id="Activation Date" />
              </Th>
              <Th onClick={handleSort} id="department">
                <Translation id="Department" />
              </Th>
              <Th onClick={handleSort} id="salaryDeductionProfile">
                <Translation id="Salary Deduction Profile" />
              </Th>
              <Th onClick={handleSort} id="devicePolicy">
                <Translation id="Device Policy" />
              </Th>
            </tr>
          </thead>
          <tbody>
            {visibleList.map(
              ({
                id,
                name,
                contactNumber,
                departmentCode,
                employeeNumber,
                phoneNo,
                activationDate,
                department,
                devicePolicy,
                salaryDeductionProfile,
              }) => (
                <tr key={`${id}`}>
                  <Td>
                    <Highlight text={name} pattern={searchFilter} />
                    <div className="mt-[0.3em] text-xs text-gray-400">
                      <Highlight text={employeeNumber} pattern={searchFilter} />
                    </div>
                  </Td>
                  <Td>
                    <Highlight text={contactNumber} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={phoneNo} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight
                      text={formatDate({
                        date: activationDate,
                        locale,
                      })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td>
                    <Highlight text={department} pattern={searchFilter} />
                    <div className="mt-[0.3em] text-xs text-gray-400">
                      <Highlight text={departmentCode} pattern={searchFilter} />
                    </div>
                  </Td>
                  <Td>
                    <Highlight text={salaryDeductionProfile} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={devicePolicy} pattern={searchFilter} />
                  </Td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      ) : (
        !isReportListLoading && <NoResults />
      )}
      <ListLoaderComponent />
    </>
  )
}
